import { AgendaItem, AgendaType } from '@/types';

export const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

export const addMonths = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth() + n, 1);

export const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export interface MonthCell {
  date: Date;
  out: boolean;
}

/**
 * Constrói 42 células (6 semanas) para uma grade mensal completa,
 * incluindo overflow do mês anterior e do próximo.
 */
export function buildMonthCells(cursor: Date): MonthCell[] {
  const first = startOfMonth(cursor);
  const dowFirst = first.getDay(); // 0 = domingo
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells: MonthCell[] = [];

  // Overflow anterior
  for (let i = dowFirst; i > 0; i--) {
    cells.push({
      date: new Date(cursor.getFullYear(), cursor.getMonth(), 1 - i),
      out: true,
    });
  }
  // Mês corrente
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({
      date: new Date(cursor.getFullYear(), cursor.getMonth(), d),
      out: false,
    });
  }
  // Completa até 42 (6 semanas)
  while (cells.length < 42) {
    const last = cells[cells.length - 1].date;
    cells.push({
      date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1),
      out: true,
    });
  }
  return cells;
}

/** Agrupa eventos por dia (chave = toDateString()). Ordena por hora. */
export function groupByDay(items: AgendaItem[]): Record<string, AgendaItem[]> {
  const m: Record<string, AgendaItem[]> = {};
  items.forEach(it => {
    const k = new Date(it.date).toDateString();
    (m[k] ||= []).push(it);
  });
  Object.values(m).forEach(a => a.sort((x, y) => (x.hour ?? 0) - (y.hour ?? 0)));
  return m;
}

export function countByType(items: AgendaItem[]): Record<AgendaType, number> {
  const s: Record<AgendaType, number> = {
    edital: 0, certidao: 0, contrato: 0, sessao: 0,
  };
  items.forEach(it => { s[it.type]++; });
  return s;
}

export function daysUntilNextHigh(items: AgendaItem[]): number | null {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const high = items
    .filter(i => i.priority === 'high' && new Date(i.date) >= today)
    .map(i => new Date(i.date))
    .sort((a, b) => +a - +b)[0];
  if (!high) return null;
  return Math.round((+high - +today) / 86_400_000);
}

/** Filtra eventos de um mês específico (cursor = dia 1). */
export function eventsInMonth(items: AgendaItem[], cursor: Date): AgendaItem[] {
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  return items.filter(it => {
    const d = new Date(it.date);
    return d.getFullYear() === y && d.getMonth() === m;
  });
}

/** Próximos N eventos a partir de hoje. */
export function upcomingEvents(items: AgendaItem[], n = 10): AgendaItem[] {
  const now = Date.now();
  return items
    .filter(i => new Date(i.date).getTime() >= now)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, n);
}
